import 'url-search-params-polyfill';
import 'whatwg-fetch';

import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import createHistory from 'history/createBrowserHistory';
import smoothscroll from 'smoothscroll-polyfill';
import { ThemeProvider as SCThemeProvider } from 'styled-components';

import { ThemeConstellation } from '@zillow/constellation';
import { IconFont } from '@zillow/zrs-zsg-icon';

import { Auth } from '@zg-rentals/auth-comet';
import { WebSocketWrapper } from '@zg-rentals/particles-js-websocket-wrapper';
import { hydrate, QueryClient, QueryClientProvider, ReactQueryDevtools } from '@zg-rentals/react-query5';
import { RentalManagerBrowserEnvironment, urlUtils } from '@zg-rentals/rental-manager-env';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { getPathWithPersistedQueryParams, parsePathAndQueryParams, parseQueryString } from '@zg-rentals/string-utils';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { setClient as setMaintenanceTrackerClient } from '@zg-rentals/ts-maintenance-tracker-client';
import { setClient as setRentalManagerApiBetaClient } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { setClient as setRinsApiClient } from '@zg-rentals/ts-rins-client';
import { setClient as setZCalendarClient } from '@zg-rentals/ts-z-calendar-client';

import RMWProvider from '../shared/providers/RMWProvider';
import AppSelectors from '../shared/selectors/AppSelectors';

import { PROXY_PATH } from '../shared/constants/RouteConstants';

import App from '../shared/containers/App';

import '@zillow/constellation-fonts/legacy-zillow-fonts.css';
import '@zillow/constellation-fonts/zillow-fonts.css';

import configureStore from '../shared/configureStore';
import pathsManifest from '../shared/pathsManifest';
import { bootstrap } from './bootstrap';
import { createZgApiHttpClient } from './zgApiHttpClient';

smoothscroll.polyfill();

/**
 * set clients outside of ZRM
 *
 * proxit endpoint use /rental path
 */
setMaintenanceTrackerClient(
  getClient().extend({ prefixUrl: __DEV__ ? 'https://www.qa.zillow.net/rental' : '/rental' }),
);
setZCalendarClient(getClient().extend({ prefixUrl: __DEV__ ? 'https://www.qa.zillow.net/rental' : '/rental' }));
setRinsApiClient(getClient().extend({ prefixUrl: __DEV__ ? 'https://www.qa.zillow.net/rental' : '/rental' }));
setRentalManagerApiBetaClient(
  getClient().extend({
    prefixUrl: __DEV__ ? `/${PROXY_PATH}` : '/',
  }),
);

const history = createHistory();
const store = configureStore(window.__INITIAL_STATE__, history);

window.store = store;
window.browserHistory = history;

const { env } = store.getState();

function resolveRentalManagerApi() {
  let zgApi = env.zgApi;
  if (zgApi.includes('https://rental-manager-api.hotpads.com')) {
    zgApi = 'https://zillow.com';
  }
  if (!zgApi.endsWith('/')) {
    zgApi = `${zgApi}/`;
  }
  return zgApi;
}

const zgApi = resolveRentalManagerApi();

const isDev = !env.isProduction;

const isStaging = window.location.hostname.includes('testpads.net');
const isQA = window.location.hostname.includes('qa.zillow.net');

// setup rental-manager-env instance
RentalManagerBrowserEnvironment.setInstance({
  brand: 'zillow',
  paths: undefined,
  isScreenDesktop: false,
  nodeEnv: {
    ip: window.store.getState().app.ip,
    brand: 'zillow',
    isInMobileWebView: false,
    originalServerSideRequestCookies: {},
    apiResponseServerSideRequestCookies: [],
    buildId: __BUILD_NUMBER__,
    zgApi,
  },
  logger: bootstrap.logger,
  isClient: true,
  isServer: false,

  // Always proxy requests in development mode, and also for any staging environments besides QA/Comet1
  proxyPath: isDev || (isStaging && !isQA) ? '/rental-manager/proxy' : undefined,
});

/*
 * Event before route changes
 *
 * @param {obj} location - The location to be transferred to
 * @param {string} action - The action is one of PUSH, REPLACE, or POP
 *     depending on how the user got to the current URL.
 * @returns {bool}
 */
window.browserHistory.block((location, action) => {
  const { pathname: targetPath, search: targetSearchQueryParams, state = {} } = location;

  if (action === 'PUSH' && state.persistQueryParams !== true) {
    const currentSearchQueryParams = window.document.location.search;

    // add target search to whitelist
    const queryParamObj = parseQueryString(targetSearchQueryParams);
    const tempWhiteList = Object.keys(queryParamObj);

    // rebuild full path
    const fullPath = `${targetPath}${currentSearchQueryParams}${targetSearchQueryParams}`;

    // set path with persisted query params
    const targetNavPath = getPathWithPersistedQueryParams(fullPath, tempWhiteList);

    // timeout is required otherwise query param is not displayed in url
    setTimeout(() => {
      window.browserHistory.replace(targetNavPath, {
        persistQueryParams: true,
        ...state,
      });
    }, 0);
  }

  return true;
});

window.router = {
  getPath(t) {
    let tempPath = '';

    if (typeof t === 'string') {
      tempPath = t;
    } else if (typeof t === 'object') {
      const { pathname = '', search = '' } = t;
      tempPath = `${pathname}${search}`;
    }

    const locationObj = parsePathAndQueryParams(tempPath);
    const { queryParams } = locationObj;

    const queryParamObj = parseQueryString(queryParams);
    const tempWhiteList = Object.keys(queryParamObj);
    const fullPath = `${tempPath}`;

    return getPathWithPersistedQueryParams(fullPath, tempWhiteList);
  },

  updateLocation(path, target = '_self') {
    if (target === '_blank') {
      window.open(path, target, 'noopener, noreferrer');
    } else {
      window.open(path, target);
    }
  },

  redirectTo(t) {
    const path = this.getPath(t);
    return window.browserHistory.replace(path);
  },
  /**
   *
   * @param {string} t - target path
   * @param {object} additionalData - object to be passed to the next route
   *
   * Object that gets used via useLocation hook
   * ex: const { state } = useLocation();
   */
  transitionTo(t, additionalData = {}, scrollY = 0) {
    const path = this.getPath(t);

    if (Object.entries(additionalData).length) {
      window.browserHistory.push(path, additionalData);
    } else {
      window.browserHistory.push(path);
    }

    setTimeout(() => {
      window.scroll(0, scrollY);
    }, 0);
  },
  /**
   *
   * @param {string} completeURL - a complete url containing protocol, hostname, path, and/or params.
   * Use if user needs to be routed to an external url belonging to a different application (a.k.a not RMW).
   */
  routeToExternalAppUrl(completeURL) {
    window.location.replace(completeURL);
  },
};

const setupRentalManagerApiWebSocket = (webSocketHost) => {
  if (__DEV__) {
    window.rentalManagerApiWebSocket = {
      addEventListener: () => {},
      removeEventListener: () => {},
      close: () => {},
    };
  } else {
    window.rentalManagerApiWebSocket = new WebSocketWrapper({
      url: `wss://${webSocketHost}/rental-manager-api/ws/services`,

      // wait up to 5 minutes before trying to reconnect again
      // resets on successfully conn
      // wait time doubles every failed conn attempt
      maxReconnect: 60000 * 5,
    });
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30 * 1000,
    },
  },
});

if (window.__QUERY_CLIENT__) {
  hydrate(queryClient, window.__QUERY_CLIENT__);
}

const LocalApp = () => {
  useBootstrap(async () => ({ bootstrap }));

  const state = window.store.getState();
  const { brand, cookieEnabledServices } = state.app;
  const { hostApp } = cookieEnabledServices;
  const paths = pathsManifest.getAllPaths(brand);
  const isInMobileWebView = hostApp === 'android' || hostApp === 'ios';
  const hostName = AppSelectors.makeHostname(state);
  const webSocketHost = urlUtils.removeProtocol(hostName);

  setupRentalManagerApiWebSocket(webSocketHost);

  return (
    <RpErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Auth.Provider
          client={createZgApiHttpClient(env.zgApiPublic)}
          includeCanClaimFreeListings={true}
          queryOptions={__DEV__ ? { refetchInterval: false } : undefined}
        >
          <Provider store={window.store}>
            <SCThemeProvider theme={ThemeConstellation}>
              <RMWProvider brand={brand} isInMobileWebView={isInMobileWebView} paths={paths}>
                <IconFont />
                <ConnectedRouter history={window.browserHistory}>
                  <App />
                </ConnectedRouter>
              </RMWProvider>
            </SCThemeProvider>
          </Provider>

          {cookieEnabledServices.ZRM_REACT_QUERY_DEVTOOLS && <ReactQueryDevtools />}
        </Auth.Provider>
      </QueryClientProvider>
    </RpErrorBoundary>
  );
};

const container = document.getElementById('app-root');
const root = createRoot(container);
root.render(<LocalApp />);

export default LocalApp;
